import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as XLSX from 'xlsx';
import cityExplorerService from '../../services/cityExplorerService';
import ViewerLoading from './ViewerLoading';

// Guardrail for a future oversized data set; today's files are 18-134 rows.
// Past this, the table stops being a useful preview and the download is the
// better answer.
const MAX_PREVIEW_ROWS = 500;

// Cells arrive as display strings (raw: false), so "1,234", "$1,200" and
// "45.6%" have to be read back as numbers or a metrics column would sort
// lexically - putting 9 after 10.
const toNumber = (value) => {
  const cleaned = String(value).trim().replace(/[$,\s%]/g, '');
  if (!/^-?\d*\.?\d+$/.test(cleaned)) return null;
  return Number(cleaned);
};

const compareValues = (a, b) => {
  const left = toNumber(a);
  const right = toNumber(b);
  if (left !== null && right !== null) return left - right;
  // numeric: true keeps "School 10" after "School 2" in text columns too.
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

const isBlank = (value) => String(value ?? '').trim() === '';

// Excel stores long identifiers as numbers carrying a scientific-notation
// format, so the displayed text rounds every NCES id in a sheet down to a
// handful of distinct strings. Where that happens the underlying value is the
// only readable - and sortable - one.
const SCIENTIFIC_NOTATION = /^-?\d*\.?\d+e[+-]?\d+$/i;

const preferRaw = (display, raw) =>
  SCIENTIFIC_NOTATION.test(String(display).trim()) && !isBlank(raw) ? String(raw) : display;

const SpreadsheetViewer = ({ locationId, resource, documentation, onOpenDocumentation }) => {
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // column: null means the sheet's own row order, which is often deliberate,
  // so sorting cycles back to it rather than trapping the reader in asc/desc.
  const [sort, setSort] = useState({ column: null, direction: 'asc' });

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setActiveSheet(0);
    setSort({ column: null, direction: 'asc' });

    (async () => {
      try {
        const buffer = await cityExplorerService.getResourceContent(locationId, resource.id);
        const workbook = XLSX.read(buffer, { type: 'array' });
        // header: 1 keeps the sheet's own column order and tolerates
        // duplicate header names; raw: false renders dates and percentages
        // the way Excel displays them.
        const readSheet = (name, raw) =>
          XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, raw, defval: '', blankrows: false });

        const parsed = workbook.SheetNames.map((name) => {
          const display = readSheet(name, false);
          const raw = readSheet(name, true);
          return {
            name,
            rows: display.map((row, r) => row.map((cell, c) => preferRaw(cell, raw[r]?.[c]))),
          };
        });
        if (active) setSheets(parsed);
      } catch (err) {
        if (active) setError(err.message || 'Could not read this file');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [locationId, resource.id]);

  const sheet = sheets[activeSheet];
  const [header, bodyRows] = useMemo(() => {
    if (!sheet?.rows?.length) return [[], []];
    return [sheet.rows[0], sheet.rows.slice(1)];
  }, [sheet]);

  // Sort the whole sheet before truncating, so the preview shows the real top
  // of the ordering rather than the first 500 rows reshuffled.
  const sortedRows = useMemo(() => {
    if (sort.column === null) return bodyRows;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...bodyRows].sort((a, b) => {
      const left = a[sort.column];
      const right = b[sort.column];
      // Empty cells sink in both directions; a block of blanks on top is
      // never the answer the reader was after.
      if (isBlank(left) || isBlank(right)) {
        if (isBlank(left) && isBlank(right)) return 0;
        return isBlank(left) ? 1 : -1;
      }
      return compareValues(left, right) * factor;
    });
  }, [bodyRows, sort]);

  const truncated = sortedRows.length > MAX_PREVIEW_ROWS;
  const visibleRows = truncated ? sortedRows.slice(0, MAX_PREVIEW_ROWS) : sortedRows;

  const handleSort = (columnIndex) => {
    setSort((prev) => {
      if (prev.column !== columnIndex) return { column: columnIndex, direction: 'asc' };
      if (prev.direction === 'asc') return { column: columnIndex, direction: 'desc' };
      return { column: null, direction: 'asc' };
    });
  };

  if (loading) {
    return (
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <ViewerLoading label={`Loading ${resource.label}\u2026`} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!sheet || !header.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">This file has no readable rows. Download it to open in Excel.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {sheets.length > 1 && (
        <Tabs
          value={activeSheet}
          onChange={(event, next) => {
            setActiveSheet(next);
            setSort({ column: null, direction: 'asc' });
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', minHeight: 40 }}
        >
          {sheets.map((s, index) => (
            <Tab key={s.name} label={s.name} value={index} sx={{ minHeight: 40, textTransform: 'none' }} />
          ))}
        </Tabs>
      )}

      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {bodyRows.length.toLocaleString()} rows &middot; {header.length} columns
          {truncated && ` \u00b7 showing the first ${MAX_PREVIEW_ROWS.toLocaleString()} - download for the full file`}
        </Typography>

        {documentation && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
            Columns use the analysis field names.
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => onOpenDocumentation?.(documentation)}
              sx={{ font: 'inherit', fontWeight: 600 }}
            >
              See {documentation.label}
            </Link>
            for definitions.
          </Typography>
        )}
      </Box>

      <TableContainer sx={{ flexGrow: 1 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {header.map((cell, index) => (
                <TableCell
                  key={index}
                  sortDirection={sort.column === index ? sort.direction : false}
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap', bgcolor: 'grey.50' }}
                >
                  <TableSortLabel
                    active={sort.column === index}
                    direction={sort.column === index ? sort.direction : 'asc'}
                    onClick={() => handleSort(index)}
                  >
                    {cell}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, rowIndex) => (
              <TableRow key={rowIndex} hover>
                {header.map((_, colIndex) => {
                  const value = row[colIndex] ?? '';
                  return (
                    <TableCell
                      key={colIndex}
                      // Some cells hold long comma-joined grade lists; clip
                      // them so one column cannot blow out the layout, but
                      // keep the full text available on hover.
                      title={String(value)}
                      sx={{
                        whiteSpace: 'nowrap',
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SpreadsheetViewer;
